import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Bell, Search, ArrowUpDown, ArrowUp, ArrowDown, CheckCheck, Check,
  RotateCcw, X, RefreshCw, Inbox, Loader2,
} from 'lucide-react';
import notificationService from '../../services/notificationService';
import {
  formatEventType, siteOf, participantOf, studyOf,
} from './notificationNavigation';
import NotificationDetailModal, { typeChipClass } from './NotificationDetailModal';
import PageHeader from '../Shared/PageHeader';
import EmptyState from '../ui/EmptyState';

const PAGE_SIZE = 25;

const getSelectedSponsor = () => {
  try {
    const raw = localStorage.getItem('selectedSponsor');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Column key -> backend ordering field.
const ORDER_FIELD = {
  is_read: 'read_at',
  event_type_key: 'event_type__key',
  title: 'title',
  site: 'site__site_code',
  participant: 'participant__participant_number',
  study: 'study_id',
  created_at: 'created_at',
};

// Percentage widths + table-fixed keep the table inside the viewport;
// the unspecified Notification column absorbs the remaining ~33%.
const COLUMNS = [
  { key: 'is_read', label: 'Status', sortable: true, width: 'w-[8%]' },
  { key: 'event_type_key', label: 'Type', sortable: true, width: 'w-[12%]' },
  { key: 'title', label: 'Notification', sortable: true },
  { key: 'site', label: 'Site', sortable: true, width: 'w-[8%]' },
  { key: 'participant', label: 'Participant', sortable: true, width: 'w-[11%]' },
  { key: 'study', label: 'Study', sortable: true, width: 'w-[9%]' },
  { key: 'created_at', label: 'Date', sortable: true, width: 'w-[13%]' },
  { key: 'actions', label: '', sortable: false, width: 'w-[6%]' },
];

// Small uppercase label above each filter.
const FILTER_LABEL = 'mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400';

const Select = ({ label, value, onChange, options }) => {
  const id = `nf-${label.toLowerCase()}`;
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className={FILTER_LABEL}>{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input w-auto min-w-[9rem]"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

const NotificationsPage = () => {
  const sponsor = getSelectedSponsor();
  const sponsorId = sponsor?.id || null;

  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [count, setCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [facets, setFacets] = useState({ event_types: [], sites: [], participants: [], studies: [] });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ type: '', status: '', site: '', participant: '', study: '' });
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [page, setPage] = useState(1);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever the query changes.
  useEffect(() => { setPage(1); }, [filters, search, sort, sponsorId]);

  const buildParams = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (sponsorId) params.sponsor = sponsorId;
    if (search) params.search = search;
    if (filters.type) params.event_type_key = filters.type;
    if (filters.status) params.is_read = filters.status === 'read';
    if (filters.site) params.site = filters.site;
    if (filters.participant) params.participant = filters.participant;
    if (filters.study) params.study = filters.study;
    const field = ORDER_FIELD[sort.key] || 'created_at';
    const primary = `${sort.dir === 'desc' ? '-' : ''}${field}`;
    // `-id` tiebreaker keeps pages from overlapping when sort values tie.
    params.ordering = field === 'id' ? primary : `${primary},-id`;
    return params;
  }, [page, sponsorId, search, filters, sort]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await notificationService.getNotifications(buildParams());
      setRows(data.results || []);
      setCount(typeof data.count === 'number' ? data.count : (data.results || []).length);
    } catch (e) {
      setError('Could not load notifications.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchUnread = useCallback(async () => {
    setUnreadCount(await notificationService.getUnreadCount(sponsorId));
  }, [sponsorId]);

  const fetchFacets = useCallback(async () => {
    setFacets(await notificationService.getFacets(sponsorId));
  }, [sponsorId]);

  useEffect(() => { fetchPage(); }, [fetchPage]);
  useEffect(() => { fetchUnread(); fetchFacets(); }, [fetchUnread, fetchFacets]);

  const options = useMemo(() => ({
    type: (facets.event_types || []).map((k) => ({ value: k, label: formatEventType(k) })),
    site: (facets.sites || []).map((s) => ({ value: String(s.value), label: s.label })),
    participant: (facets.participants || []).map((p) => ({ value: String(p.value), label: `P${p.label}` })),
    study: (facets.studies || []).map((s) => ({ value: String(s.value), label: s.label || `Study ${s.value}` })),
  }), [facets]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeFilters = Object.values(filters).some(Boolean) || search;

  const toggleSort = (key) => {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'created_at' ? 'desc' : 'asc' }));
  };

  const setReadState = async (n, read) => {
    try {
      if (read) await notificationService.markAsRead([n.id]);
      else await notificationService.markAsUnread([n.id]);
      setRows((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: read } : x)));
      fetchUnread();
    } catch { /* leave row as-is on failure */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead(sponsorId);
      setRows((prev) => prev.map((x) => ({ ...x, is_read: true })));
      fetchUnread();
    } catch { /* no-op */ }
  };

  const handleRowClick = (n) => {
    if (!n.is_read) setReadState(n, true);
    setDetail(n);
  };

  const clearFilters = () => {
    setFilters({ type: '', status: '', site: '', participant: '', study: '' });
    setSearchInput('');
  };

  const refresh = () => { fetchPage(); fetchUnread(); fetchFacets(); };

  // Stacked date/time keeps the Date column narrow enough to fit on one screen.
  const formatDay = (d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (d) => new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const SortIcon = ({ col }) => {
    if (!col.sortable) return null;
    if (sort.key !== col.key) return <ArrowUpDown size={13} className="ml-1 inline text-gray-300 dark:text-gray-600" />;
    return sort.dir === 'asc'
      ? <ArrowUp size={13} className="ml-1 inline text-primary-600 dark:text-primary-400" />
      : <ArrowDown size={13} className="ml-1 inline text-primary-600 dark:text-primary-400" />;
  };

  return (
    <div>
      {/* Header */}
      <PageHeader
        icon={Bell}
        title="Notifications"
        subtitle={(
          <>
            {sponsor?.name ? <span className="font-medium text-gray-700 dark:text-gray-200">{sponsor.name}</span> : 'All sponsors'}
            {' · '}{count}{activeFilters ? ' matching' : ' total'}
            {unreadCount > 0 && <span className="font-medium text-primary-700 dark:text-primary-400"> · {unreadCount} unread</span>}
          </>
        )}
      >
        <button
          onClick={refresh}
          className="btn btn-secondary"
        >
          <RefreshCw size={16} /> Refresh
        </button>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="btn btn-primary"
          >
            <CheckCheck size={16} /> Mark all read
          </button>
        )}
      </PageHeader>

      {/* Toolbar */}
      <div className="card mb-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col flex-1 min-w-[14rem]">
            <label htmlFor="nf-search" className={FILTER_LABEL}>Search</label>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                id="nf-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search title or body…"
                className="input pl-9 pr-3"
              />
            </div>
          </div>
          <Select label="Type" value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} options={options.type} />
          <Select
            label="Status" value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={[{ value: 'unread', label: 'Unread' }, { value: 'read', label: 'Read' }]}
          />
          <Select label="Site" value={filters.site} onChange={(v) => setFilters((f) => ({ ...f, site: v }))} options={options.site} />
          <Select label="Participant" value={filters.participant} onChange={(v) => setFilters((f) => ({ ...f, participant: v }))} options={options.participant} />
          <Select label="Study" value={filters.study} onChange={(v) => setFilters((f) => ({ ...f, study: v }))} options={options.study} />
          {activeFilters && (
            <button onClick={clearFilters} className="btn btn-ghost h-10">
              <X size={16} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && toggleSort(col.key)}
                    className={`th-cell px-3 whitespace-nowrap overflow-hidden ${col.width || ''} ${col.sortable ? 'cursor-pointer select-none transition-colors hover:text-gray-700 dark:hover:text-gray-200' : ''}`}
                  >
                    {col.label}<SortIcon col={col} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={COLUMNS.length} className="px-4 py-16 text-center">
                  <Loader2 className="mx-auto h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
                  <p className="mt-3 text-gray-500 dark:text-gray-400">Loading notifications…</p>
                </td></tr>
              ) : error ? (
                <tr><td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-danger-600 dark:text-danger-400">{error}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={COLUMNS.length} className="p-0">
                  <EmptyState
                    icon={Inbox}
                    title={activeFilters ? 'No notifications match your filters' : 'No notifications yet'}
                  />
                </td></tr>
              ) : rows.map((n) => (
                  <motion.tr
                    key={n.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => handleRowClick(n)}
                    className={`cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.03] ${!n.is_read ? 'bg-primary-50/50 dark:bg-primary-500/[0.06]' : ''}`}
                  >
                    <td className="px-3 py-3">
                      {n.is_read ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500"><Check size={13} /> Read</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 dark:text-primary-400">
                          <span className="h-2 w-2 rounded-full bg-primary-500" /> New
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 overflow-hidden">
                      <span className={`inline-block max-w-full truncate rounded-full px-2 py-0.5 text-xs font-medium ${typeChipClass(n.event_type_key)}`}>
                        {formatEventType(n.event_type_key)}
                      </span>
                    </td>
                    <td className="px-3 py-3 overflow-hidden">
                      <p className={`${!n.is_read ? 'font-semibold text-gray-900 dark:text-gray-50' : 'text-gray-700 dark:text-gray-300'} truncate`}>{n.title}</p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{n.body}</p>
                    </td>
                    <td className="px-3 py-3 truncate text-gray-600 dark:text-gray-300">{siteOf(n) || '—'}</td>
                    <td className="px-3 py-3 truncate text-gray-600 dark:text-gray-300">{participantOf(n) || '—'}</td>
                    <td className="px-3 py-3 truncate text-gray-600 dark:text-gray-300">{studyOf(n) || '—'}</td>
                    <td className="px-3 py-3 text-gray-500 dark:text-gray-400">
                      <div className="whitespace-nowrap">{formatDay(n.created_at)}</div>
                      <div className="whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">{formatTime(n.created_at)}</div>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setReadState(n, !n.is_read)}
                        title={n.is_read ? 'Mark unread' : 'Mark read'}
                        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600 dark:text-gray-500 dark:hover:bg-white/[0.06] dark:hover:text-primary-400"
                      >
                        {n.is_read ? <RotateCcw size={16} /> : <Check size={16} />}
                      </button>
                    </td>
                  </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && count > 0 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <span className="tabular-nums">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of {count}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary btn-sm"
              >Prev</button>
              <span className="px-2 tabular-nums">Page {page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn btn-secondary btn-sm"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {detail && (
        <NotificationDetailModal notification={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

export default NotificationsPage;
